/**
 * Tenant Resolver — determines which tenant the current user belongs to.
 *
 * Used by chat surfaces and other components to resolve tenant context
 * before making API calls or configuring the orchestrator.
 *
 * Resolution order:
 * 1. Check user's tenant_membership in Firestore
 * 2. Default to null (no tenant — uses global defaults)
 *
 * The URL fallback (/justiceisurright → tenant justiceisurright) went with that page (retired 2026-10-24; its address
 * forwards to /desktop). The firm's own people carry the tenant on their membership, so nothing reads the address.
 */

import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getApp } from 'firebase/app';

export interface TenantMembership {
  tenantId: string;
  role: 'admin' | 'staff' | 'viewer';
  status: 'active' | 'suspended' | 'invited';
}

// Cache to avoid repeated Firestore reads within a session
let cachedMembership: TenantMembership | null | undefined = undefined;

/**
 * Get the current user's tenant membership from Firestore.
 * Returns null if user has no tenant, undefined if not authenticated.
 */
export async function getUserTenantMembership(): Promise<TenantMembership | null> {
  if (cachedMembership !== undefined) return cachedMembership;

  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    cachedMembership = null;
    return null;
  }

  try {
    const db = getFirestore(getApp());
    const membershipSnap = await getDoc(doc(db, `users/${user.uid}/tenant_membership/main`));

    if (!membershipSnap.exists()) {
      cachedMembership = null;
      return null;
    }

    const data = membershipSnap.data() as TenantMembership;
    if (data.status !== 'active') {
      cachedMembership = null;
      return null;
    }

    cachedMembership = data;
    return data;
  } catch (err) {
    console.warn('[getUserTenantMembership] Failed to read membership', err);
    cachedMembership = null;
    return null;
  }
}

/**
 * Resolve the tenant ID for the current context: the user's membership, or none.
 */
export async function resolveTenantId(): Promise<string | null> {
  const membership = await getUserTenantMembership();
  return membership?.tenantId || null;
}

/**
 * Invalidate the cached membership. Call after login/logout or tenant changes.
 */
export function invalidateTenantCache(): void {
  cachedMembership = undefined;
}
